/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DiffUIState } from './types';
import { BUTTON_ZOOM_SCALE, DiffUiDisplayType } from './constants';
import { ImageType } from '../../global/constants';
import { zoomToCenter, zoomToFitStage } from './utils';
import { RectFragFragment } from '../../graphql/__generated__/graphql';

export const getInitialState = (): DiffUIState => ({
  syncPan: true,
  offset: {
    x: 0,
    y: 0,
  },
  zoom: 1,
  stageDims: {
    height: 1,
    width: 1,
  },
  dims: {
    [ImageType.BASELINE]: {
      height: null,
      width: null,
    },
    [ImageType.SNAPSHOT]: {
      height: null,
      width: null,
    },
  },
  displayType: DiffUiDisplayType.SideBySide,
  diffSettings: {
    hideDiffs: false,
    emphasizeDiffs: false,
  },
  singleDisplayType: ImageType.SNAPSHOT,
  pulseLocation: null,
});

const slice = createSlice({
  name: 'visual/diffUi',
  initialState: getInitialState(),
  reducers: {
    setOffset: (state, action: PayloadAction<DiffUIState['offset']>) => {
      state.offset = action.payload;
    },
    scrollToDiff: (state, action: PayloadAction<RectFragFragment>) => {
      const rect = action.payload;
      const { zoom, stageDims } = state;
      const { height, width } = stageDims;

      const x = (-rect.x - (rect.width / 2)) * zoom;
      const y = (-rect.y - (rect.height / 2)) * zoom;

      // Trigger an on-screen pulse to show the user where the diff is
      state.pulseLocation = {
        x: rect.x + (rect.width / 2),
        y: rect.y + (rect.height / 2),
        timestamp: new Date().getTime(),
      };

      // Update the stage position to put the diff in the center of the screen
      state.offset = {
        x: x + ((width ?? 0) / 2),
        y: y + ((height ?? 0) / 2),
      };
    },
    toggleDisplayType: (state) => {
      state.displayType = state.displayType === DiffUiDisplayType.Single ? (
        DiffUiDisplayType.SideBySide
      ) : DiffUiDisplayType.Single;
    },
    toggleSingleView: (state) => {
      state.singleDisplayType = state.singleDisplayType === ImageType.SNAPSHOT ? (
        ImageType.BASELINE
      ) : ImageType.SNAPSHOT;
    },
    toggleEmphasizeDiffs: (state) => {
      state.diffSettings.emphasizeDiffs = !state.diffSettings.emphasizeDiffs;
    },
    toggleHideDiffs: (state) => {
      state.diffSettings.hideDiffs = !state.diffSettings.hideDiffs;
    },
    // zoom / scale
    resetZoom: (state) => {
      zoomToCenter(state, 1);
    },
    setZoom: (state, action: PayloadAction<number>) => {
      state.zoom = action.payload;
    },
    zoomIn: (state) => {
      zoomToCenter(state, state.zoom * BUTTON_ZOOM_SCALE);
    },
    zoomOut: (state) => {
      zoomToCenter(state, state.zoom / BUTTON_ZOOM_SCALE);
    },
    zoomToFit: (state, action: PayloadAction<boolean | undefined>) => {
      const { payload: enlarge } = action;
      zoomToFitStage(state, enlarge);
    },
    // pan
    togglePanSync: (state) => {
      state.syncPan = !state.syncPan;
    },
    // stage
    setStageDims: (state, action: PayloadAction<DiffUIState['stageDims']>) => {
      state.stageDims = action.payload;
      zoomToFitStage(state);
    },
    // images
    setImageDims: (state, action: PayloadAction<{
      type: ImageType,
      dims: {
        height: number | null,
        width: number | null,
      }
    }>) => {
      const { type, dims } = action.payload;
      state.dims[type] = dims;
    },
  },
});

export const { actions } = slice;

export default slice.reducer;
